import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Client } from '../../domain/models/client.model';
import { ClientListQuery } from '../../domain/models/client-list-query.model';
import { ClientListResult } from '../../domain/models/client-list-result.model';
import { ClientUpdateInput } from '../../domain/models/client-update-input.model';
import {
  ClientConsultationSectionDataMap, ClientConsultationSectionKey,
} from '../../domain/models/clinical-history.model';
import { ConsultationAttachmentFile } from '@features/web/patients/domain/models/clinical-history.model';
import { Consent } from '../../domain/models/consent.model';
import { ClientAppointment } from '../../domain/models/client-appointment.model';
import { ClientMetrics } from '../../domain/models/client-metrics.model';
import { DepartmentTransferResult } from '@features/shared/departments/domain/models/department-transfer-result.model';
import { toDepartmentTransferResult } from '@features/shared/departments/data/mappers/department-transfer-result.mapper';
import {
  ClientConsultationSummariesResult, ClientEstimatesResult, ClientInvoicesResult, IClientsRepository,
} from '../../domain/repositories/clients.repository';
import { ClientsRemoteDatasource } from '../datasources/remote/clients.remote-datasource';
import { ClientMapper } from '../mappers/client.mapper';
import { ClientClinicalHistoryMapper } from '../mappers/clinical-history.mapper';
import { InvoiceMapper } from '../mappers/invoice.mapper';
import { EstimateMapper } from '../mappers/estimate.mapper';
import { ConsentMapper } from '../mappers/consent.mapper';
import { ClientAppointmentMapper } from '../mappers/client-appointment.mapper';
import { ClientProfessionalMapper } from '../mappers/client-professional.mapper';
import { ClientProfessional } from '../../domain/models/client-professional.model';

const APPOINTMENT_LOOKBACK_DAYS = 30;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const APPOINTMENT_PAGE_SIZE = 50;

// Chronological, undated entries last: ProVet returns appointments in its own id order, and the
// picker reads top-down from the closest cita onwards.
function compareByStart(left: ClientAppointment, right: ClientAppointment): number {
  if (!left.start) return right.start ? 1 : 0;
  if (!right.start) return -1;
  return left.start.localeCompare(right.start);
}

@Injectable()
export class ClientsRepositoryImpl implements IClientsRepository {
  private readonly datasource = inject(ClientsRemoteDatasource);
  private readonly mapper = inject(ClientMapper);
  private readonly clinicalHistoryMapper = inject(ClientClinicalHistoryMapper);
  private readonly invoiceMapper = inject(InvoiceMapper);
  private readonly estimateMapper = inject(EstimateMapper);
  private readonly consentMapper = inject(ConsentMapper);
  private readonly clientAppointmentMapper = inject(ClientAppointmentMapper);
  private readonly clientProfessionalMapper = inject(ClientProfessionalMapper);

  public list(query: ClientListQuery): Observable<ClientListResult> {
    const source$ = query.search
      ? this.datasource.search(
        query.search, query.page, query.pageSize, query.sortBy, query.sortDir, query.filters, query.departmentId,
      )
      : this.datasource.list(
        query.page, query.pageSize, query.sortBy, query.sortDir, query.filters, query.departmentId,
      );

    return source$.pipe(
      map(dto => ({
        items: dto.Items.map(item => this.mapper.toClient(item)),
        total: dto.TotalCount,
        asOfUtc: dto.AsOfUtc ?? null,
      })),
    );
  }

  public getById(id: string): Observable<Client> {
    return this.datasource.getById(id).pipe(map(dto => this.mapper.toClient(dto)));
  }

  public getMetrics(departmentId?: number): Observable<ClientMetrics> {
    return this.datasource.getMetrics(departmentId).pipe(map(dto => this.mapper.toClientMetrics(dto)));
  }

  public create(input: ClientUpdateInput, departmentId?: number): Observable<Client> {
    const payload = this.mapper.toCreatePayload(input);
    return this.datasource.create(payload, departmentId).pipe(map(dto => this.mapper.toClient(dto)));
  }

  public update(id: string, changes: ClientUpdateInput): Observable<Client> {
    const payload = this.mapper.toUpdatePayload(changes);
    return this.datasource.update(id, payload).pipe(map(dto => this.mapper.toClient(dto)));
  }

  public delete(id: string): Observable<void> {
    return this.datasource.delete(id);
  }

  public activate(id: string): Observable<Client> {
    return this.datasource.activate(id).pipe(map(dto => this.mapper.toClient(dto)));
  }

  public moveToDepartment(id: string, departmentId: number): Observable<DepartmentTransferResult> {
    return this.datasource.moveToDepartment(id, departmentId).pipe(map(dto => toDepartmentTransferResult(dto)));
  }

  public claimConflictingClient(id: string, departmentId: number, email: string): Observable<DepartmentTransferResult> {
    return this.datasource.claimDepartment(id, departmentId, email).pipe(map(dto => toDepartmentTransferResult(dto)));
  }

  public getConsultationSummaries(
    clientId: string, organizationId: number, page: number, pageSize: number, patientId?: string,
  ): Observable<ClientConsultationSummariesResult> {
    return this.datasource.getConsultationSummaries(clientId, organizationId, page, pageSize, patientId).pipe(
      map(dto => ({
        items: dto.Items.map(item => this.clinicalHistoryMapper.toConsultationSummary(item)),
        total: dto.TotalCount,
      })),
    );
  }

  public getConsultationSection<K extends ClientConsultationSectionKey>(
    clientId: string, patientId: string, consultationId: string, section: K,
  ): Observable<ClientConsultationSectionDataMap[K]> {
    return this.datasource.getConsultationSection(clientId, patientId, consultationId, section).pipe(
      map(dto => this.clinicalHistoryMapper.toConsultationSection(section, dto)),
    );
  }

  public getInvoices(clientId: string, page: number, pageSize: number): Observable<ClientInvoicesResult> {
    return this.datasource.getInvoices(clientId, page, pageSize).pipe(
      map(dto => ({
        items: dto.Items.map(item => this.invoiceMapper.toInvoice(item)),
        total: dto.TotalCount,
      })),
    );
  }

  public getInvoicePdf(invoiceId: string): Observable<Blob> {
    return this.datasource.getInvoicePdf(invoiceId);
  }

  public downloadConsultationAttachment(
    clientId: string, patientId: string, consultationId: string, noteId: string, uploadId: string,
  ): Observable<Blob> {
    return this.datasource.downloadConsultationAttachment(clientId, patientId, consultationId, noteId, uploadId);
  }

  public getConsultationAnalysisReferralAttachments(
    clientId: string, patientId: string, consultationId: string, referralId: string,
  ): Observable<readonly ConsultationAttachmentFile[]> {
    return this.datasource.getConsultationAnalysisReferralAttachments(clientId, patientId, consultationId, referralId).pipe(
      map(dtos => this.clinicalHistoryMapper.toConsultationAttachmentFiles(dtos)),
    );
  }

  public getEstimates(clientId: string, page: number, pageSize: number): Observable<ClientEstimatesResult> {
    return this.datasource.getEstimates(clientId, page, pageSize).pipe(
      map(dto => ({
        items: dto.Items.map(item => this.estimateMapper.toEstimate(item)),
        total: dto.TotalCount,
      })),
    );
  }

  public getConsents(organizationId: number, clientId: string): Observable<readonly Consent[]> {
    return this.datasource.getConsents(organizationId, clientId).pipe(
      map(dtos => dtos.map(dto => this.consentMapper.toConsent(dto))),
    );
  }

  public getAppointments(clientId: string): Observable<readonly ClientAppointment[]> {
    const startFrom = new Date(Date.now() - APPOINTMENT_LOOKBACK_DAYS * MILLISECONDS_PER_DAY).toISOString();
    return this.datasource.listAppointments(clientId, startFrom, APPOINTMENT_PAGE_SIZE).pipe(
      map(dto => dto.Items
        .map(item => this.clientAppointmentMapper.toClientAppointment(item))
        .filter(appointment => !!appointment.id)
        .sort(compareByStart)),
    );
  }

  public getProfessionals(): Observable<readonly ClientProfessional[]> {
    return this.datasource.listProfessionals().pipe(
      map(dto => dto.Items
        // Virtual users are agenda resources (a room, a machine), never a person who can sign off a consent.
        .filter(item => item.IsActive && !item.VirtualUser && !!item.ExternalId)
        .map(item => this.clientProfessionalMapper.toClientProfessional(item))
        .sort((left, right) => left.name.localeCompare(right.name))),
    );
  }

  public requestConsent(
    organizationId: number, clientId: string, documentType: string, version: string,
    channel: 'email' | 'app', message?: string, appointmentExternalId?: string,
  ): Observable<Consent> {
    return this.datasource
      .requestConsent(organizationId, clientId, documentType, version, channel, message, appointmentExternalId)
      .pipe(map(dto => this.consentMapper.toConsent(dto)));
  }

  public declineConsentRequest(organizationId: number, clientId: string, consentRequestId: string): Observable<Consent> {
    return this.datasource.declineConsentRequest(organizationId, clientId, consentRequestId).pipe(
      map(dto => this.consentMapper.toConsent(dto)),
    );
  }
}
